import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useDropzone } from 'react-dropzone';
import {
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  Trash2,
  FileText,
  Upload,
  File,
  Image,
  FileSpreadsheet,
  Loader2,
  Download,
  Calendar
} from 'lucide-react';
import { evidenceApi, assetsApi, requirementsApi } from '../lib/api';
import { toast } from 'sonner';
import { cn, formatDate, formatDateTime } from '../lib/utils';

const evidenceTypes = [
  { value: 'politica', label: 'Política' },
  { value: 'procedimento', label: 'Procedimento' },
  { value: 'registo', label: 'Registo' },
  { value: 'certificado', label: 'Certificado' },
  { value: 'screenshot', label: 'Screenshot' },
  { value: 'relatorio', label: 'Relatório' },
  { value: 'configuracao', label: 'Configuração de Ativo' },
  { value: 'diagrama', label: 'Diagrama de Rede' },
  { value: 'outro', label: 'Outro' },
];

const confidentialityLevels = [
  { value: 'publica', label: 'Pública' },
  { value: 'interna', label: 'Interna' },
  { value: 'confidencial', label: 'Confidencial' },
  { value: 'estritamente_confidencial', label: 'Estritamente Confidencial' },
];

const getFileIcon = (fileName) => {
  if (!fileName) return File;
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return Image;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet;
  if (['pdf'].includes(ext)) return FileText;
  return File;
};

const initialFormData = {
  name: '',
  description: '',
  evidence_type: 'politica',
  requirement_ids: [],
  asset_ids: [],
  validity_date: '',
  confidentiality: 'interna',
  tags: '',
};

export default function Evidence() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assetIdFromUrl = searchParams.get('asset_id');
  
  const [evidence, setEvidence] = useState([]);
  const [assets, setAssets] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [typeFilter]);

  useEffect(() => {
    if (assetIdFromUrl) {
      setFormData(prev => ({ ...prev, asset_ids: [assetIdFromUrl] }));
      setShowForm(true);
    }
  }, [assetIdFromUrl]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (typeFilter) params.evidence_type = typeFilter;
      
      const [evidenceRes, assetsRes, reqsRes] = await Promise.all([
        evidenceApi.getAll(params),
        assetsApi.getAll({}),
        requirementsApi.getAll({}),
      ]);
      
      setEvidence(evidenceRes.data);
      setAssets(assetsRes.data);
      setRequirements(reqsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setUploadedFile(acceptedFiles[0]);
      if (!formData.name) {
        setFormData(prev => ({ ...prev, name: acceptedFiles[0].name.replace(/\.[^/.]+$/, '') }));
      }
    }
  }, [formData.name]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('O nome é obrigatório');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        file_name: uploadedFile?.name || null,
        file_size: uploadedFile?.size || null,
        // In a real app, we'd upload the file to storage and get a URL
        file_url: uploadedFile ? `local://${uploadedFile.name}` : null,
      };

      await evidenceApi.create(payload);
      toast.success('Evidência registada com sucesso');
      setShowForm(false);
      setFormData(initialFormData);
      setUploadedFile(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao registar evidência');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (evidenceId) => {
    if (!confirm('Tem a certeza que pretende eliminar esta evidência?')) return;
    try {
      await evidenceApi.delete(evidenceId);
      toast.success('Evidência eliminada com sucesso');
      loadData();
    } catch (error) {
      toast.error('Erro ao eliminar evidência');
    }
  };

  // Filter by search
  const filteredEvidence = evidence.filter(
    (ev) =>
      ev.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ev.evidence_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats
  const stats = evidence.reduce(
    (acc, ev) => {
      acc.total++;
      acc.byType[ev.evidence_type] = (acc.byType[ev.evidence_type] || 0) + 1;
      return acc;
    },
    { total: 0, byType: {} }
  );

  const headerContent = (
    <Button onClick={() => { setFormData(initialFormData); setUploadedFile(null); setShowForm(true); }} data-testid="new-evidence-btn">
      <Plus className="h-4 w-4 mr-2" />
      Nova Evidência
    </Button>
  );

  if (isLoading) {
    return (
      <Layout title="Evidências">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Evidências" headerContent={headerContent}>
      <div className="space-y-6" data-testid="evidence-container">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="card-flat">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-xs text-slate-500">Total</p>
            </CardContent>
          </Card>
          {['politica', 'procedimento', 'certificado', 'relatorio'].map((type) => {
            const typeInfo = evidenceTypes.find(t => t.value === type);
            return (
              <Card key={type} className="card-flat">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-slate-700">{stats.byType[type] || 0}</p>
                  <p className="text-xs text-slate-500">{typeInfo?.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <Card className="card-flat">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar evidências..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-evidence-input"
                />
              </div>
              <Select
                value={typeFilter}
                onValueChange={(value) => setTypeFilter(value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Tipos</SelectItem>
                  {evidenceTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Evidence Grid */}
        {filteredEvidence.length === 0 ? (
          <Card className="card-flat">
            <CardContent className="p-8 text-center">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Nenhuma evidência encontrada</h3>
              <p className="text-sm text-slate-500 mb-4">
                {searchQuery || typeFilter ? 'Tente ajustar os filtros' : 'Comece por adicionar a primeira evidência'}
              </p>
              <Button onClick={() => setShowForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Evidência
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEvidence.map((ev) => {
              const FileIcon = getFileIcon(ev.file_name);
              const typeInfo = evidenceTypes.find(t => t.value === ev.evidence_type);
              
              return (
                <Card key={ev.evidence_id} className="card-flat hover:shadow-sm transition-shadow" data-testid={`evidence-${ev.evidence_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <FileIcon className="h-5 w-5 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-medium text-slate-900 truncate">{ev.name}</p>
                            <p className="text-xs text-slate-500 font-mono">{ev.evidence_id}</p>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {ev.file_url && (
                                <DropdownMenuItem>
                                  <Download className="h-4 w-4 mr-2" />
                                  Descarregar
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => handleDelete(ev.evidence_id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {typeInfo?.label || ev.evidence_type}
                          </Badge>
                          {ev.validity_date && (
                            <Badge variant="outline" className="text-xs flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(ev.validity_date)}
                            </Badge>
                          )}
                        </div>
                        {ev.description && (
                          <p className="text-sm text-slate-500 mt-2 line-clamp-2">{ev.description}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-2">
                          Criado em {formatDate(ev.created_at)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Evidence Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Evidência</DialogTitle>
            <DialogDescription>
              Registe uma nova evidência de conformidade
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* File Upload */}
            <div className="space-y-2">
              <Label>Ficheiro (opcional)</Label>
              <div
                {...getRootProps()}
                className={cn(
                  'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
                  isDragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-blue-400'
                )}
              >
                <input {...getInputProps()} />
                {uploadedFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <File className="h-8 w-8 text-green-600" />
                    <div className="text-left">
                      <p className="font-medium text-slate-900">{uploadedFile.name}</p>
                      <p className="text-sm text-slate-500">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">Arraste um ficheiro ou clique para selecionar</p>
                    <p className="text-xs text-slate-400">Máximo 10MB</p>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Política de Segurança v2.0"
                  required
                  data-testid="evidence-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="evidence_type">Tipo</Label>
                <Select
                  value={formData.evidence_type}
                  onValueChange={(value) => setFormData({ ...formData, evidence_type: value })}
                >
                  <SelectTrigger id="evidence_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {evidenceTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição do documento..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="confidentiality">Confidencialidade</Label>
                <Select
                  value={formData.confidentiality}
                  onValueChange={(value) => setFormData({ ...formData, confidentiality: value })}
                >
                  <SelectTrigger id="confidentiality">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {confidentialityLevels.map((l) => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="validity_date">Data de Validade</Label>
                <Input
                  id="validity_date"
                  type="date"
                  value={formData.validity_date}
                  onChange={(e) => setFormData({ ...formData, validity_date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tags">Tags (separadas por vírgula)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="Ex: iso27001, auditoria, 2024"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving} data-testid="save-evidence-btn">
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Registar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
